import type { PergolaConfig, Anclaje, TipoPergola, Extra, ColorTier, ScreenType } from './types';
import { findOption, type WizardState } from './schema';

/**
 * Convierte el estado del wizard (data-driven) en la configuración resuelta que
 * consumen el 3D y el presupuestador. Resuelve dimensiones (estándar vs
 * personalizado), altura, nº de postes y color (hex/tier) desde attributes.json.
 *
 * @param lamasOpen estado runtime del toggle de lamas (no vive en el wizard).
 */
export function resolveConfig(state: WizardState, lamasOpen = false): PergolaConfig {
  const tipo = state['tipo'] as TipoPergola;

  // Dimensiones: estándar (opción ancho-largo) o personalizado (decimales mm).
  let widthMm: number;
  let lengthMm: number;
  if (state['dimensiones-modo'] === 'personalizado') {
    widthMm = Number(state['ancho-custom']);
    lengthMm = Number(state['largo-custom']);
  } else {
    const dim = findOption('ancho-largo', state['ancho-largo'] as string);
    widthMm = dim?.ancho_mm ?? 3000;
    lengthMm = dim?.largo_mm ?? 4000;
  }

  const alturaOpt = findOption('altura', state['altura'] as string);
  const heightMm = alturaOpt?.altura_mm ?? 2300;

  const postesOpt = findOption('postes', state['postes'] as string);
  const postes = (postesOpt?.count ?? 4) as 4 | 6;

  const colorOpt = findOption('color-estructura', state['color-estructura'] as string);
  const colorHex = colorOpt?.hex ?? '#383E42';
  const colorTier = (colorOpt?.tier ?? 'estandar') as ColorTier;

  const lamasOpt = findOption('color-lamas', state['color-lamas'] as string);
  const lamasColorHex = lamasOpt?.hex ?? colorHex;
  const lamasDir: 1 | -1 = state['lamas-rotacion'] === 'derecha' ? 1 : -1;

  // Cerramientos por lado.
  const sc = (k: string): ScreenType => (state[k] as ScreenType) ?? 'ninguno';
  const screens = {
    frontal: sc('screen-frontal'),
    trasero: sc('screen-trasero'),
    izquierda: sc('screen-izquierda'),
    derecha: sc('screen-derecha'),
  };
  const screensOn = Object.values(screens).some((s) => s !== 'ninguno');

  // Iluminación / extras (toggles sí/no).
  const ledOn = state['led'] === 'si';
  const ledValue = state['led-color'] as string;
  const ledOpt = findOption('led-color', ledValue);
  const ledColorHex = ledOpt?.hex ?? '#ffce8a';
  const ledRgb = ledValue === 'rgb';
  const sensorOn = state['sensor'] === 'si';
  const motorOn = state['motor'] === 'si';

  // Lista de extras derivada (para el presupuestador).
  const extras: Extra[] = [];
  if (ledOn) extras.push('led');
  if (sensorOn) extras.push('sensor');
  if (screensOn) extras.push('screens');
  if (motorOn) extras.push('motor');

  return {
    tipo,
    widthMm,
    lengthMm,
    heightMm,
    anclaje: state['anclaje'] as Anclaje,
    postes,
    colorHex,
    colorTier,
    lamasColorHex,
    lamasDir,
    lamasOpen,
    extras,
    ledOn,
    ledColorHex,
    ledRgb,
    screensOn,
    screens,
  };
}
